import { faCheck, faXmark } from "@fortawesome/pro-regular-svg-icons";
import { Box, Button } from "@mui/material";
import { useState } from "react";

import { useFileUpload } from "@/hooks/useFileUpload";
import { dataURLtoBlob } from "@/utils/blobs";

import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogHeader,
  Divider,
  Icon,
  useTheme,
} from "..";
import { FilepickerFile } from "../FilePicker/types";
import { VideoEditorContent } from "./components/VideoEditorContent";
import { useVideoEditorState } from "./state";
import type { VideoEditorFile } from "./types";

interface VideoEditorDialogProps {
  open?: boolean;
  file: VideoEditorFile;
  onClose: () => void;
  onConfirm: (file: FilepickerFile) => void;
}

export const VideoEditorDialog = (props: VideoEditorDialogProps) => {
  const theme = useTheme();
  const { open = false, file, onClose, onConfirm } = props;
  const { upload } = useFileUpload();
  const { frames, currentFrameIndex } = useVideoEditorState();
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    setIsSubmitting(true);
    const currentFrame = frames[currentFrameIndex];

    if (!currentFrame) {
      throw new Error("No frame selected");
    }

    // Convert the data URL to a Blob
    const blob = dataURLtoBlob(currentFrame.src);

    // Create a File object from the Blob
    const frameFile = new File(
      [blob],
      `frame_${currentFrame.timestamp.toFixed(2)}s.jpg`,
      {
        type: "image/jpeg",
      },
    );

    const filePickerFile = await upload(frameFile, {
      sourceType: "REFERENCE",
    });

    setIsSubmitting(false);
    onConfirm(filePickerFile);
    onClose();
  };

  const handleLoadingStateChange = (
    loading: boolean,
    loadingError: string | null,
  ) => {
    setIsLoading(loading);
    setError(loadingError);
  };

  return (
    <Dialog fullWidth maxWidth="lg" open={open} onClose={onClose}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          backgroundColor: theme.palette.secondary[7],
        }}
      >
        <DialogHeader title="Select Video Frame" onClose={onClose} />
        <DialogContent sx={{ flex: 1, padding: 0 }}>
          <Divider />
          {!!file && (
            <VideoEditorContent
              file={file}
              onLoadingStateChange={handleLoadingStateChange}
            />
          )}
          <Divider sx={{ marginBottom: 2 }} />
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            color="secondary"
            onClick={onClose}
            startIcon={<Icon icon={faXmark} />}
          >
            Cancel
          </Button>
          <Button
            disabled={isLoading || !!error || isSubmitting}
            variant="contained"
            color="primary"
            onClick={handleConfirm}
            startIcon={<Icon icon={faCheck} />}
            loading={isSubmitting}
          >
            Extract Frame
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};
